import { ErrorState } from '../../../List/components/feedback';
import { useCRMOrder } from '../../hooks/useCRMOrder';
import { OrderInfo } from '../display/OrderInfo';
import { SelectedItems } from '../display/SelectedItems';
import { CRMStatusTagsNotes } from '../display/CRMStatusTagsNotes';
import { Tracking } from '../display/Tracking';
import { ShippingInfo } from '../display/ShippingInfo';
import { AdditionalInfo } from '../display/AdditionalInfo';
import { ViewCRMSkeleton } from '../skeletons/ViewCRMSkeleton';
import { Breadcrumb } from '../display/Breadcrumb';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@shared/components/ui/shadcn/tabs';
import { DeliveryRate } from '@modules/Orders/ViewOrders/components/display/DeliveryRate';

export const Main = () => {
  const { order, loading, error, refetch, updateOrder } = useCRMOrder();

  if (loading) {
    return <ViewCRMSkeleton />;
  }

  if (error) {
    const errorObj = new Error(error);
    return <ErrorState error={errorObj} onRetry={refetch} />;
  }

  if (!order) {
    return null;
  }

  return (
    <div className="py-4 px-6">
      <Breadcrumb orderRef={order.ref} />

      {/* Vista Mobile con Tabs */}
      <div className="md:hidden">
        <Tabs defaultValue="pedido" className="w-full">
          <TabsList className="w-full grid grid-cols-3 bg-transparent border-0 p-0 gap-0 mb-4">
            <TabsTrigger
              value="pedido"
              className="data-[state=active]:bg-hover-press data-[state=active]:text-text data-[state=inactive]:text-complementary-text rounded-none py-2 px-4 text-sm font-medium transition-colors"
            >
              Pedido
            </TabsTrigger>
            <TabsTrigger
              value="envio"
              className="data-[state=active]:bg-hover-press data-[state=active]:text-text data-[state=inactive]:text-complementary-text rounded-none py-2 px-4 text-sm font-medium transition-colors"
            >
              Envío
            </TabsTrigger>
            <TabsTrigger
              value="crm"
              className="data-[state=active]:bg-hover-press data-[state=active]:text-text data-[state=inactive]:text-complementary-text rounded-none py-2 px-4 text-sm font-medium transition-colors"
            >
              CRM
            </TabsTrigger>
          </TabsList>

          {/* Contenido Tab Pedido */}
          <TabsContent value="pedido" className="mt-0 space-y-5">
            <OrderInfo
              orderCreatedAt={order.order_created_at}
              shopName={order.shop?.name}
              shopImageHash={order.shop?.image_hash}
            />
            <SelectedItems
              lines={order.lines}
              totalShipping={order.total_shipping}
              totalDiscount={order.total_discount}
              currency={order.currency || 'EUR'}
              orderId={order.id}
              statusId={order.status_id}
              onUpdate={refetch}
            />
          </TabsContent>

          {/* Contenido Tab Envío */}
          <TabsContent value="envio" className="mt-0 space-y-5">
            <DeliveryRate />
            {order.shipment?.tracking_number && <Tracking shipment={order.shipment} />}
            <ShippingInfo
              customer={order.customer}
              orderId={order.id}
              lines={order.lines}
              statusId={order.status_id}
              onUpdate={refetch}
            />
          </TabsContent>

          {/* Contenido Tab CRM */}
          <TabsContent value="crm" className="mt-0">
            <CRMStatusTagsNotes
              orderId={order.id}
              orderRef={order.ref}
              crmStatus={order.crm_status}
              statusId={order.status_id}
              tags={order.tags || []}
              notes={order.notes || []}
              onUpdate={refetch}
              updateOrder={updateOrder}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Vista Desktop sin cambios */}
      <div className="hidden md:flex flex-col md:flex-row justify-between items-start gap-6">
        {/* Columna Izquierda */}
        <div className="w-full md:w-[70%] flex flex-col gap-5">
          <OrderInfo
            orderCreatedAt={order.order_created_at}
            shopName={order.shop?.name}
            shopImageHash={order.shop?.image_hash}
          />
          <SelectedItems
            lines={order.lines}
            totalShipping={order.total_shipping}
            totalDiscount={order.total_discount}
            currency={order.currency || 'EUR'}
            orderId={order.id}
            statusId={order.status_id}
            onUpdate={refetch}
          />
        </div>
        {/* Columna Derecha */}
        <div className="w-full md:w-[30%] flex flex-col gap-5">
          <CRMStatusTagsNotes
            orderId={order.id}
            orderRef={order.ref}
            crmStatus={order.crm_status}
            statusId={order.status_id}
            tags={order.tags || []}
            notes={order.notes || []}
            onUpdate={refetch}
            updateOrder={updateOrder}
          />
          <DeliveryRate />
          {order.shipment?.tracking_number && <Tracking shipment={order.shipment} />}
          <ShippingInfo
            customer={order.customer}
            orderId={order.id}
            lines={order.lines}
            statusId={order.status_id}
            onUpdate={refetch}
          />
          <AdditionalInfo
            paymentMethodId={order.payment_method_id}
            orderId={order.id}
            lines={order.lines}
            statusId={order.status_id}
            onUpdate={refetch}
          />
        </div>
      </div>
    </div>
  );
};
